import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import { 
  MessageCircle, 
  Video, 
  FileText, 
  FolderKanban, 
  BarChart3, 
  Search, 
  Award, 
  User 
} from 'lucide-react';

const VAHeaderNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [interviewCount, setInterviewCount] = useState(0);
  const [applicationCount, setApplicationCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  const navigationItems = [
    {
      id: 'messages',
      label: 'Messages',
      icon: MessageCircle,
      path: '/messages',
      description: 'Conversations with employers',
      count: messageCount
    },
    {
      id: 'interviews',
      label: 'Interviews',
      icon: Video,
      path: '/interviews',
      description: 'Schedule and manage interviews',
      count: interviewCount
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: FileText,
      path: '/applications',
      description: 'View and manage your job applications',
      count: applicationCount
    },
    {
      id: 'projects',
      label: 'My Contracts',
      icon: FolderKanban,
      path: '/my-contracts',
      description: 'Track work hours and manage timesheets',
      count: projectCount
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
      description: 'Performance insights and goals'
    },
    {
      id: 'jobs',
      label: 'Find Jobs',
      icon: Search,
      path: '/job-search',
      description: 'Browse and apply to jobs'
    },
    {
      id: 'skills',
      label: 'Skills',
      icon: Award,
      path: '/up-skills',
      description: 'Skills assessment and development'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      path: '/va-profile',
      description: 'Manage your profile and portfolio'
    }
  ];

  // Fetch initial counts
  const fetchInitialCounts = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      // Fetch unread message count
      const { count: msgCount, error: messageError } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', currentUser.id)
        .eq('is_read', false);

      if (!messageError) {
        setMessageCount(msgCount || 0);
      }

      // Fetch upcoming interview count
      const now = new Date().toISOString();
      const { count: intCount, error: interviewError } = await supabase
        .from('interview_schedules')
        .select('*', { count: 'exact', head: true })
        .eq('candidate_id', currentUser.id)
        .eq('status', 'scheduled')
        .gte('scheduled_at', now);

      if (!interviewError) {
        setInterviewCount(intCount || 0);
      }

      // Fetch pending applications count
      const { count: appCount, error: applicationError } = await supabase
        .from('job_applications')
        .select('*', { count: 'exact', head: true })
        .eq('applicant_id', currentUser.id)
        .in('status', ['pending']);

      if (!applicationError) {
        setApplicationCount(appCount || 0);
      }

      // Fetch active projects count via project_assignments
      const { count: projCount, error: projectError } = await supabase
        .from('project_assignments')
        .select(`
          project_id,
          projects!inner(
            id,
            status
          )
        `, { count: 'exact', head: true })
        .eq('va_id', currentUser.id)
        .eq('projects.status', 'active');

      if (!projectError) {
        setProjectCount(projCount || 0);
      }
    } catch (error) {
      console.error('[VAHeaderNav] Error fetching initial counts:', error);
    }
  }, [currentUser?.id]);

  // Fetch project count separately for real-time updates
  const fetchProjectCount = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const { count: projCount, error: projectError } = await supabase
        .from('project_assignments')
        .select(`
          project_id,
          projects!inner(
            id,
            status
          )
        `, { count: 'exact', head: true })
        .eq('va_id', currentUser.id)
        .eq('projects.status', 'active');

      if (!projectError) {
        setProjectCount(projCount || 0);
      }
    } catch (error) {
      console.error('[VAHeaderNav] Error fetching project count:', error);
    }
  }, [currentUser?.id]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!currentUser?.id) return;

    fetchInitialCounts();

    // Subscribe to message changes
    const messagesChannel = supabase
      .channel('header_nav_messages')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'messages',
          filter: `recipient_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          if (eventType === 'INSERT' && !newRecord.is_read) {
            setMessageCount(prev => prev + 1);
          } else if (eventType === 'UPDATE') {
            if (oldRecord.is_read === false && newRecord.is_read === true) {
              setMessageCount(prev => Math.max(0, prev - 1));
            } else if (oldRecord.is_read === true && newRecord.is_read === false) {
              setMessageCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE' && !oldRecord.is_read) {
            setMessageCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    // Subscribe to interview changes
    const interviewsChannel = supabase
      .channel('header_nav_interviews')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'interview_schedules',
          filter: `candidate_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const now = new Date();
          
          if (eventType === 'INSERT') {
            if (newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'UPDATE') {
            const wasUpcoming = oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now;
            const isUpcoming = newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now;
            
            if (wasUpcoming && !isUpcoming) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            } else if (!wasUpcoming && isUpcoming) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE') {
            if (oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to application changes
    const applicationsChannel = supabase
      .channel('header_nav_applications')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'job_applications',
          filter: `applicant_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const pendingStatuses = ['pending'];
          
          if (eventType === 'INSERT') {
            if (pendingStatuses.includes(newRecord.status)) {
              setApplicationCount(prev => prev + 1);
            }
          } else if (eventType === 'UPDATE') {
            const wasPending = pendingStatuses.includes(oldRecord.status);
            const isPending = pendingStatuses.includes(newRecord.status);
            
            if (wasPending && !isPending) {
              setApplicationCount(prev => Math.max(0, prev - 1));
            } else if (!wasPending && isPending) {
              setApplicationCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE') {
            if (pendingStatuses.includes(oldRecord.status)) {
              setApplicationCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to project assignment changes
    const projectsChannel = supabase
      .channel('header_nav_projects')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'project_assignments',
          filter: `va_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType } = payload;
          
          if (eventType === 'INSERT' || eventType === 'DELETE') {
            // When a new assignment is created/removed, refresh the count
            fetchProjectCount();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(interviewsChannel);
      supabase.removeChannel(applicationsChannel);
      supabase.removeChannel(projectsChannel);
    };
  }, [currentUser?.id, fetchInitialCounts]);

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  // If no user is logged in, don't render the navigation header
  if (!currentUser) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-30">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Desktop Navigation - Full text labels (lg and above) */}
        <div className="hidden lg:flex justify-center space-x-1 overflow-x-auto">
          {navigationItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`relative flex items-center space-x-2 px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActivePath(item.path)
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
              title={item.description}
            >
              <item.icon size={16} className="flex-shrink-0" />
              <span>{item.label}</span>
              {item.count > 0 && (
                <span className="absolute -top-0 -right-1 bg-red-500 mt-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {item.count > 99 ? '99+' : item.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tablet Navigation - Icon-only squeezed (md to lg) */}
        <div className="hidden md:flex lg:hidden justify-center items-center gap-4 space-x-1 overflow-x-auto py-2 px-2">
          {navigationItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`relative flex flex-col items-center justify-center px-2 py-1.5 rounded-lg transition-all flex-shrink-0 ${
                isActivePath(item.path)
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title={item.description}
            >
              <item.icon size={18} className="flex-shrink-0" />
              <span className="text-xs mt-0.5 text-center max-w-10 truncate">{item.label}</span>
              {item.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse text-0.5">
                  {item.count > 9 ? '9+' : item.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VAHeaderNav;
